// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyFile } from "@/types";
import Icon from "@/UI/atoms/Icon";
import UploadFilesPopup from "@/UI/popups/UploadFilesPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    /** The record these pictures belong to. It has to exist — a file needs an owner. */
    owner: { model: string; id: string };
    /** What they are **to** it: its gallery, its portrait, its logo. Written onto every upload. */
    slot: (typeof constants.fileSlots)[number];
    /** Chosen values, in order. One for a portrait, several for a gallery. */
    value: string[];
    onChange: (next: string[]) => void;
    /**
     * What to store. A public owner's file has a permanent address, so the record keeps the `url`;
     * a private one has none and keeps the `id`, which is signed on read. See `customerPicture`.
     */
    emit?: "url" | "id";
    /** `1` turns picking into replacing, which is what a portrait or a logo wants. */
    max?: number;
    label?: string;
    hint?: string;
    disabled?: boolean;
};

/**
 * The record's own pictures, picked from the drive.
 *
 * **This is where a gallery is curated**, not where it comes from. The API answers `gallery` from
 * the record's `slot: "gallery"` files, so a photograph shows the moment it lands — see
 * `functions/galleryPhotos`. What this control writes back is the **order and the subset**: a
 * gallery has a first picture and a set has none, and the first picture is the cover.
 *
 * So the slot says which of the record's images are gallery material; the list this writes says
 * which of those were chosen and in what sequence. Both are true and each has a job.
 *
 * Only this slot's images are offered. A customer's ID scan is not a portrait, and a project's
 * floor plan is not a gallery picture — mixing them is exactly the muddle `File.slot` exists to end.
 */
export default function ImagePickerInput({ owner, slot, value, onChange, emit = "url", max, label, hint, disabled }: Props) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);
    const uploadsFinishedAt = useUploads((state) => state.finishedAt);

    const shots = useFetch<{ files: TyFile[] }>("/drive", {}, false);

    const load = () => {
        if (!owner.id) return;
        shots.invoke({ query: { ownerModel: owner.model, ownerId: owner.id, category: "image", slot, limit: constants.maxPageSize } });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [owner.model, owner.id, slot]);

    // A new picture finishes in the tray, which this hears about rather than waits for.
    useEffect(() => {
        if (!uploadsFinishedAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadsFinishedAt]);

    const openUpload = () =>
        addComp({
            name: "upload-pictures",
            Component: UploadFilesPopup,
            place: "end",
            anim: "endSlide",
            details: { owner: { model: owner.model, id: owner.id }, slot },
        });

    /** What the record would store for this file — the two halves of the same pointer. */
    const keyOf = (file: TyFile): string | null => (emit === "id" ? file._id : file.url);

    const toggle = (file: TyFile) => {
        const key = keyOf(file);
        if (!key) return;

        if (value.includes(key)) return onChange(value.filter((item) => item !== key));
        // At the cap, picking replaces rather than refusing: a portrait is a choice, not a queue.
        if (max === 1) return onChange([key]);
        if (max && value.length >= max) return;
        onChange([...value, key]);
    };

    /** A private file has no permanent address, so the signed thumbnail is the only thing to draw. */
    const pictures = (shots.data?.files ?? []).filter((file) => file.thumb?.url || file.url);

    return (
        <div className="image-picker">
            {label && (
                <span className="image-picker__label">
                    {label}
                    {hint && <small>{hint}</small>}
                </span>
            )}

            <div className="image-picker__grid">
                {pictures.map((file) => {
                    const key = keyOf(file);
                    const at = key ? value.indexOf(key) : -1;

                    return (
                        <button
                            key={file._id}
                            type="button"
                            className={`image-picker__shot${at >= 0 ? " image-picker__shot--on" : ""}`}
                            onClick={() => toggle(file)}
                            disabled={disabled}
                            title={file.name}
                            aria-pressed={at >= 0}
                        >
                            <img src={file.thumb?.url ?? file.url ?? ""} alt={file.name} loading="lazy" />
                            {/* The number is the gallery's order, which is the whole reason the
                                record keeps a list rather than deriving a set. */}
                            {at >= 0 && <span className="image-picker__order">{max === 1 ? <Icon name="check" size={13} /> : at + 1}</span>}
                        </button>
                    );
                })}

                <button
                    type="button"
                    className="image-picker__shot image-picker__shot--add"
                    onClick={openUpload}
                    disabled={disabled}
                    title={t.drive.upload}
                >
                    <Icon name="upload" size={18} />
                </button>
            </div>

            {!shots.loading && !pictures.length && <p className="image-picker__empty">{t.drive.emptySlot}</p>}
        </div>
    );
}
